/**
 * SC-05: 案件編集
 *
 * 既存の営業案件を編集する画面。新規作成画面と同じテンプレートを使用し、
 * 編集不可フィールドをdisabledに設定
 *
 * @created 2025-11-16
 * @updated 2025-11-18
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from '@mui/material';
import { ArrowBack, Save, CheckCircle, Edit as EditIcon } from '@mui/icons-material';
import type { CloseReason, AccountSearchResult } from '../../services/salesApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { salesCasesApi, salesMasterApi, salesApprovalsApi } from '../../services/salesApi';
import type { SalesCase, SalesPhase, ResourceAllocation, Approver } from '../../services/salesApi';
import { ActivityTimeline } from '../../components/Sales/ActivityTimeline';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import AsyncAutocomplete from '../../components/Common/AsyncAutocomplete';
import ResourceAllocationTable from '../../components/Sales/ResourceAllocationTable';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';
import RequiredBadge from '../../components/Common/RequiredBadge';

interface Department {
  department_id: string;
  department_name: string;
}

const SalesCaseEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const { user, salesPermissions } = useAuth();
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [salesCase, setSalesCase] = useState<SalesCase | null>(null);
  const [phases, setPhases] = useState<SalesPhase[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountSearchResult | string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReasons, setCloseReasons] = useState<CloseReason[]>([]);
  const [closeResult, setCloseResult] = useState<'won' | 'lost' | undefined>('won');
  const [selectedCloseReasonCodes, setSelectedCloseReasonCodes] = useState<string[]>([]);
  const [closeReasonText, setCloseReasonText] = useState('');

  // 原価システム整合性確認ダイアログ
  const [costSystemConfirmOpen, setCostSystemConfirmOpen] = useState(false);
  const [pendingPhaseChange, setPendingPhaseChange] = useState<number | null>(null);

  // 担当者変更用（実行フェーズ移行時）
  const [changeAssignee, setChangeAssignee] = useState(false);
  const [newAssigneeId, setNewAssigneeId] = useState('');
  const [assigneeList, setAssigneeList] = useState<{ employee_id: string; name: string }[]>([]);
  const [isExecutionPhaseChange, setIsExecutionPhaseChange] = useState(false);

  // 承認ワークフロー用
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [originalResourcesData, setOriginalResourcesData] = useState<Partial<ResourceAllocation>[]>([]);

  // フォームデータ
  const [caseName, setCaseName] = useState('');
  const [caseOverview, setCaseOverview] = useState('');
  const [status, setStatus] = useState('active');
  const [accountId, setAccountId] = useState<number | ''>('');
  const [serviceDivision, setServiceDivision] = useState('');
  const [endUser, setEndUser] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [salesPerson, setSalesPerson] = useState('');
  const [salesPersonId, setSalesPersonId] = useState('');
  const [currentPhase, setCurrentPhase] = useState<number | ''>('');
  const [originalPhase, setOriginalPhase] = useState<number | ''>('');
  const [initialRegistrationReason, setInitialRegistrationReason] = useState('');
  const [phaseChangeReason, setPhaseChangeReason] = useState('');
  const [expectedOrderAmount, setExpectedOrderAmount] = useState('');
  const [currentYearPlannedSales, setCurrentYearPlannedSales] = useState('');
  const [grossProfit, setGrossProfit] = useState('');
  const [isGrossProfitManuallyEdited, setIsGrossProfitManuallyEdited] = useState(false);
  const [externalProjectId, setExternalProjectId] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [nextActionMemo, setNextActionMemo] = useState('');
  const [nextActionMode, setNextActionMode] = useState<'view' | 'edit'>('edit');
  const [actionCompleting, setActionCompleting] = useState(false);

  // 行動履歴の状態管理
  const [activities, setActivities] = useState<any[]>([]);

  // 要員計画データ
  const [resourcesData, setResourcesData] = useState<Partial<ResourceAllocation>[]>([]);
  // 指定年度（変更可能）
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const [fiscalYear, setFiscalYear] = useState(() => currentYear);

  useEffect(() => {
    fetchPhases();
    fetchCloseReasons();
    fetchDepartments();
    if (caseId) {
      fetchCaseDetail();
      fetchActivities();
      fetchAllocations();
    }
  }, [caseId]);

  // 要員配置データ取得
  const fetchAllocations = async () => {
    try {
      const response = await salesCasesApi.getCaseAllocations(parseInt(caseId!));
      if (response.data.success) {
        const toFormAllocations = (rows: typeof response.data.data) => rows.map(a => ({
          resource_name: a.resource_name,
          resource_type: a.resource_type,
          employee_id: a.employee_id || '',
          vendor_id: a.vendor_id,
          unit_price: a.unit_price,
          cost_rate: a.cost_rate,
          start_month: a.start_month,
          end_month: a.end_month,
        }));
        const allocations = toFormAllocations(response.data.data);
        setResourcesData(allocations);
        const approvedRows = response.data.approved_allocations;
        setOriginalResourcesData(approvedRows ? toFormAllocations(approvedRows) : allocations);
      }
    } catch (err) {
      console.error('要員配置取得エラー:', err);
    }
  };

  // 承認者リスト取得
  const fetchApprovers = async () => {
    try {
      const response = await salesApprovalsApi.getApprovers();
      if (response.data.success) {
        setApprovers(response.data.data);
      }
    } catch (err) {
      console.error('承認者リスト取得エラー:', err);
    }
  };

  const fetchCloseReasons = async () => {
    try {
      const response = await salesMasterApi.getCloseReasons();
      if (response.data.success) {
        setCloseReasons(response.data.data.all);
      }
    } catch (err) {
      console.error('クローズ理由取得エラー:', err);
    }
  };

  const fetchPhases = async () => {
    try {
      const response = await salesMasterApi.getPhases();
      if (response.data.success) {
        setPhases(response.data.data);
      }
    } catch (err) {
      console.error('フェーズ取得エラー:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/departments', {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setDepartments(data.data || []);
      }
    } catch (err) {
      console.error('部署取得エラー:', err);
    }
  };

  // 取引先選択時の処理（freeSoloで文字列も許可）
  const handleAccountSelect = (account: AccountSearchResult | string | null) => {
    setSelectedAccount(account);
    // オブジェクトの場合はaccount_idをセット、文字列の場合は空にする
    if (account && typeof account !== 'string') {
      setAccountId(account.account_id);
    } else {
      setAccountId('');
    }
  };

  const fetchCaseDetail = async () => {
    try {
      setLoading(true);
      const response = await salesCasesApi.getCaseById(parseInt(caseId!));

      if (response.data.success) {
        const data = response.data.data;

        // 権限チェック: 担当者または営業マスタ管理者のみ編集可能
        const isAssignedEmployee = data.assigned_employee_id === user?.employee_id;
        const isAccountAdmin = salesPermissions?.can_manage_accounts;

        if (!isAssignedEmployee && !isAccountAdmin) {
          // 権限がない場合は詳細画面にリダイレクト
          setError('この案件を編集する権限がありません（担当者または営業マスタ管理者のみ編集可能です）');
          setTimeout(() => {
            navigate(`/sales/cases/${caseId}`);
          }, 2000);
          return;
        }

        setSalesCase(data);
        setCaseName(data.case_name);
        setCaseOverview(data.case_summary || '');
        setStatus(data.status);
        setAccountId(data.account_id);
        // 取引先情報を復元
        if (data.account_id && data.account_name) {
          setSelectedAccount({
            account_id: data.account_id,
            account_name: data.account_name,
          });
        }
        setServiceDivision(data.service_department || '');
        setEndUser(data.end_user || '');
        // case_sourceは廃止予定（契約履歴から自動判定へ移行）
        setBusinessCategory(data.business_category || '');
        setSalesPerson(data.assigned_employee_name || data.assigned_employee_id);
        setSalesPersonId(data.assigned_employee_id);
        setCurrentPhase(data.sales_phase_id);
        setOriginalPhase(data.approved_values?.sales_phase_id ?? data.sales_phase_id);
        setInitialRegistrationReason(data.initial_registration_reason || '');
        setExpectedOrderAmount(data.expected_order_amount?.toString() || '');
        // 受注予定月はearliestStartMonthから自動計算されるため、読み込み不要

        setCurrentYearPlannedSales(data.current_year_planned_sales?.toString() || '');
        setGrossProfit(data.gross_profit?.toString() || '');
        setExternalProjectId(data.external_project_id || '');

        // 次回アクション日の形式変換（Date型またはISO文字列 → YYYY-MM-DD）
        if (data.next_action_date) {
          const actionDateStr = typeof data.next_action_date === 'string'
            ? data.next_action_date.substring(0, 10)
            : (() => { const d = new Date(data.next_action_date); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
          setNextActionDate(actionDateStr);
          setNextActionMode('view');
        } else {
          setNextActionDate('');
          setNextActionMode('edit');
        }
        setNextActionMemo(data.next_action_memo || '');

        if (data.close_result) {
          setCloseResult(data.close_result as 'won' | 'lost');
        }
        if (data.close_reason_codes) {
          setSelectedCloseReasonCodes(data.close_reason_codes);
        }
        if (data.close_reason) {
          setCloseReasonText(data.close_reason);
        }
      }
    } catch (err) {
      console.error('案件詳細取得エラー:', err);
      setError(extractErrorMessage(err, '案件詳細の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await salesCasesApi.getCaseActivities(parseInt(caseId!));
      if (response.data.success) {
        setActivities(response.data.data);
      }
    } catch (err) {
      console.error('行動履歴取得エラー:', err);
    }
  };

  const handleAddActivity = async (activity: any) => {
    try {
      await salesCasesApi.addCaseActivity(parseInt(caseId!), activity);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴追加エラー:', err);
      throw err;
    }
  };

  const handleUpdateActivity = async (activityId: number, activity: any) => {
    try {
      await salesCasesApi.updateCaseActivity(parseInt(caseId!), activityId, activity);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴更新エラー:', err);
      throw err;
    }
  };

  const handleDeleteActivity = async (activityId: number) => {
    try {
      await salesCasesApi.deleteCaseActivity(parseInt(caseId!), activityId);
      await fetchActivities();
    } catch (err) {
      console.error('行動履歴削除エラー:', err);
      throw err;
    }
  };

  // プリセールスフェーズの判定（他のロジックで使用するため先に定義）
  const isPresalesPhase = useMemo(() => {
    if (!currentPhase) return false;
    const phase = phases.find(p => p.phase_id === currentPhase);
    return phase?.phase_code === 'PRESALES';
  }, [currentPhase, phases]);

  // 実施フェーズかどうかのチェック（外部プロジェクトID必須判定用）
  // カバレッジ、実行、生産移管フェーズでは外部プロジェクトIDが必須
  const isExecutionPhase = useMemo(() => {
    if (!currentPhase) return false;
    const phase = phases.find(p => p.phase_id === currentPhase);
    return ['COVERAGE', 'EXECUTION', 'PRODUCTION_TRANSFER'].includes(phase?.phase_code || '');
  }, [currentPhase, phases]);

  // 要員配置から計算した金額を表示用に算出
  const calculatedFromResources = useMemo(() => {
    let totalSales = 0;
    let totalCost = 0;

    resourcesData.forEach(r => {
      if (r.start_month && r.end_month) {
        const start = new Date(r.start_month);
        const end = new Date(r.end_month);
        // 月数を計算
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
        totalSales += (Number(r.unit_price) || 0) * months;
        totalCost += (Number(r.cost_rate) || 0) * months;
      }
    });

    return { sales: totalSales, cost: totalCost, grossProfit: totalSales - totalCost };
  }, [resourcesData]);

  // 要員配置から最も早い開始月を取得
  const earliestStartMonth = useMemo(() => {
    if (resourcesData.length === 0) return '';

    const startMonths = resourcesData
      .filter(r => r.start_month)
      .map(r => r.start_month as string);

    if (startMonths.length === 0) return '';

    // 最も早い月を取得（YYYY-MM形式でソート可能）
    return startMonths.sort()[0].substring(0, 7); // YYYY-MMに正規化
  }, [resourcesData]);

  // 指定年度の月別予実計算（要員配置から自動計算）
  const monthlyBreakdown = useMemo(() => {
    const months: Array<{
      month: number;
      plannedSales: number;
      plannedCost: number;
      plannedOutsourceRate: number;
      actualSales: number;
      actualCost: number;
      actualOutsourceRate: number;
    }> = [];

    for (let m = 1; m <= 12; m++) {
      let plannedSales = 0;
      let plannedCost = 0;
      let plannedOutsourceCost = 0;

      resourcesData.forEach(r => {
        if (r.start_month && r.end_month) {
          // YYYY-MM or YYYY-MM-DD format から年月を抽出（タイムゾーン問題を回避）
          const startYear = parseInt(r.start_month.substring(0, 4));
          const startMonth = parseInt(r.start_month.substring(5, 7));
          const endYear = parseInt(r.end_month.substring(0, 4));
          const endMonth = parseInt(r.end_month.substring(5, 7));

          // ターゲット年月が範囲内かチェック（YYYYMM形式で比較）
          const targetYearMonth = fiscalYear * 100 + m;
          const startYearMonth = startYear * 100 + startMonth;
          const endYearMonth = endYear * 100 + endMonth;

          if (targetYearMonth >= startYearMonth && targetYearMonth <= endYearMonth) {
            plannedSales += Number(r.unit_price) || 0;
            plannedCost += Number(r.cost_rate) || 0;
            // 外注タイプの場合は外注コストとしてカウント
            if (r.resource_type === 'contractor' || r.resource_type === 'outsource_pool') {
              plannedOutsourceCost += Number(r.cost_rate) || 0;
            }
          }
        }
      });

      const plannedOutsourceRate = plannedCost > 0 ? (plannedOutsourceCost / plannedCost) * 100 : 0;

      months.push({
        month: m,
        plannedSales,
        plannedCost,
        plannedOutsourceRate,
        actualSales: 0, // 実績は月次予実タブから取得（将来拡張）
        actualCost: 0,
        actualOutsourceRate: 0,
      });
    }

    return months;
  }, [resourcesData, fiscalYear]);

  // 指定年度の合計計算
  const yearlyTotals = useMemo(() => {
    const totals = monthlyBreakdown.reduce(
      (acc, m) => ({
        plannedSales: acc.plannedSales + m.plannedSales,
        plannedCost: acc.plannedCost + m.plannedCost,
        actualSales: acc.actualSales + m.actualSales,
        actualCost: acc.actualCost + m.actualCost,
      }),
      { plannedSales: 0, plannedCost: 0, actualSales: 0, actualCost: 0 }
    );
    return {
      ...totals,
      plannedGrossProfit: totals.plannedSales - totals.plannedCost,
      actualGrossProfit: totals.actualSales - totals.actualCost,
    };
  }, [monthlyBreakdown]);

  // 粗利額の自動計算（受注予定額の20%）
  useEffect(() => {
    if (expectedOrderAmount && !isGrossProfitManuallyEdited) {
      const calculatedGrossProfit = parseFloat(expectedOrderAmount) * 0.2;
      setGrossProfit(calculatedGrossProfit.toFixed(0));
    }
  }, [expectedOrderAmount, isGrossProfitManuallyEdited]);

  // プリセールスフェーズの場合、金額情報を自動的に0に設定
  useEffect(() => {
    if (isPresalesPhase) {
      setExpectedOrderAmount('0');
      setCurrentYearPlannedSales('0');
      setGrossProfit('0');
    }
  }, [isPresalesPhase]);
  // 受注予定月はearliestStartMonthから自動計算されるため、状態管理は不要

  // 粗利額の手動変更ハンドラ
  const handleGrossProfitChange = (value: string) => {
    setGrossProfit(value);
    if (value) {
      setIsGrossProfitManuallyEdited(true);
    }
  };

  // 選択中フェーズのおすすめアクション
  const suggestedActions = useMemo(() => {
    if (!currentPhase) return [];
    const phase = phases.find(p => p.phase_id === currentPhase);
    return phase?.suggested_next_actions || [];
  }, [currentPhase, phases]);

  // 当年度期待売上額（自動計算）
  const calculatedYearlyExpectedSales = useMemo(() => {
    if (!currentYearPlannedSales || !currentPhase) return 0;
    const phase = phases.find(p => p.phase_id === currentPhase);
    if (!phase) return 0;
    return parseFloat(currentYearPlannedSales) * ((phase.individual_conversion_rate ?? 0) / 100);
  }, [currentYearPlannedSales, currentPhase, phases]);

  // 期待売上（自動計算）
  const calculatedExpectedContribution = useMemo(() => {
    if (!expectedOrderAmount || !currentPhase) return 0;
    const phase = phases.find(p => p.phase_id === currentPhase);
    if (!phase) return 0;
    return parseFloat(expectedOrderAmount) * ((phase.individual_conversion_rate ?? 0) / 100);
  }, [expectedOrderAmount, currentPhase, phases]);

  // エラー時にページ先頭へスクロール
  const scrollToTop = () => {
    pageTopRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const setErrorAndScroll = (message: string) => {
    setError(message);
    scrollToTop();
  };

  // 担当者リスト取得
  const fetchAssigneeList = async () => {
    try {
      const assignees = await salesCasesApi.searchAssignees('', 100);
      setAssigneeList(assignees);
    } catch (err) {
      console.error('担当者リスト取得エラー:', err);
    }
  };

  // フェーズ変更ハンドラー（実施フェーズへの変更時に確認ダイアログを表示）
  const handlePhaseChange = async (phaseId: number) => {
    const targetPhase = phases.find(p => p.phase_id === phaseId);
    const isTargetExecution = targetPhase?.phase_code === 'EXECUTION';
    const isTargetCoverageOrExecution = ['COVERAGE', 'EXECUTION', 'PRODUCTION_TRANSFER'].includes(targetPhase?.phase_code || '');
    
    // 現在のフェーズが実行フェーズかどうか確認（カバレッジは含まない）
    const currentPhaseObj = phases.find(p => p.phase_id === currentPhase);
    const isCurrentExecution = ['EXECUTION', 'PRODUCTION_TRANSFER'].includes(currentPhaseObj?.phase_code || '');
    const isCurrentCoverage = currentPhaseObj?.phase_code === 'COVERAGE';

    // 実行フェーズへの変更はカバレッジからのみ許可
    if (isTargetExecution && !isCurrentCoverage && !isCurrentExecution) {
      setError('実行フェーズへの変更はカバレッジフェーズからのみ可能です。先にカバレッジに変更してください。');
      return;
    }
    
    // 実行フェーズへの変更時はダイアログを表示（カバレッジ→実行も含む）
    if (isTargetExecution && !isCurrentExecution) {
      setPendingPhaseChange(phaseId);
      setIsExecutionPhaseChange(true);
      
      // 担当者リストを取得
      await fetchAssigneeList();
      
      setCostSystemConfirmOpen(true);
    } else if (isTargetCoverageOrExecution && !isCurrentExecution && !isTargetExecution) {
      // カバレッジへの移行（従来の原価確認ダイアログのみ）
      setPendingPhaseChange(phaseId);
      setIsExecutionPhaseChange(false);
      setCostSystemConfirmOpen(true);
    } else {
      setCurrentPhase(phaseId);
      
      // カバレッジ/実行フェーズ以外に変更する場合、受注状態から商談中に戻す
      if (!isTargetCoverageOrExecution && status === 'closed' && closeResult === 'won') {
        setStatus('active');
        setCloseResult(undefined);
      }
    }
  };

  // 原価システム確認ダイアログの確認処理
  const handleCostSystemConfirm = () => {
    if (pendingPhaseChange !== null) {
      setCurrentPhase(pendingPhaseChange);
      
      // カバレッジ/実行フェーズへの変更時は自動的に受注確定に設定
      const targetPhase = phases.find(p => p.phase_id === pendingPhaseChange);
      if (targetPhase && ['COVERAGE', 'EXECUTION'].includes(targetPhase.phase_code || '')) {
        setStatus('closed');
        setCloseResult('won');
      }
      
      // 担当者変更がある場合は反映
      if (changeAssignee && newAssigneeId) {
        setSalesPersonId(newAssigneeId);
        const newAssignee = assigneeList.find(a => a.employee_id === newAssigneeId);
        if (newAssignee) {
          setSalesPerson(newAssignee.name);
        }
      }
    }
    setCostSystemConfirmOpen(false);
    setPendingPhaseChange(null);
    setChangeAssignee(false);
    setNewAssigneeId('');
    setIsExecutionPhaseChange(false);
  };

  // 原価システム確認ダイアログのキャンセル処理
  const handleCostSystemCancel = () => {
    setCostSystemConfirmOpen(false);
    setPendingPhaseChange(null);
    setChangeAssignee(false);
    setNewAssigneeId('');
    setIsExecutionPhaseChange(false);
  };

  const handleActionComplete = async () => {
    if (!nextActionDate || !nextActionMemo) {
      setError('完了するアクションの日付と内容が必要です');
      return;
    }
    setActionCompleting(true);
    try {
      await salesCasesApi.addCaseActivity(parseInt(caseId!), {
        activity_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
        activity_type: '商談',
        description: nextActionMemo,
      });
      await fetchActivities();
      setNextActionDate('');
      setNextActionMemo('');
      setNextActionMode('edit');
      setSuccess('アクションを行動履歴に登録しました。新しいネクストアクションを入力してください。');
    } catch (err) {
      console.error('行動履歴登録エラー:', err);
      setError(extractErrorMessage(err, '行動履歴の登録に失敗しました'));
    } finally {
      setActionCompleting(false);
    }
  };

  const handleActionPlanChange = () => {
    setNextActionMode('edit');
  };

  const validateForm = (): boolean => {
    if (!caseName) {
      setErrorAndScroll('案件名は必須です');
      return false;
    }
    if (!caseOverview) {
      setErrorAndScroll('案件概要は必須です');
      return false;
    }
    if (!accountId) {
      setErrorAndScroll('取引先は必須です');
      return false;
    }
    // case_sourceは廃止予定のため検証不要
    if (!currentPhase) {
      setErrorAndScroll('営業フェーズは必須です');
      return false;
    }

    // フェーズが変更されている場合、変更理由が必須
    if (currentPhase !== originalPhase && !phaseChangeReason.trim()) {
      setErrorAndScroll('フェーズを変更する場合、変更理由は必須です');
      return false;
    }

    // プリセールス以外のフェーズでは要員計画が必須
    if (!isPresalesPhase && resourcesData.length === 0) {
      setErrorAndScroll('プリセールス以外のフェーズでは要員計画の入力が必須です');
      return false;
    }
    // 金額は要員配置から動的計算されるためバリデーション不要

    // 事業カテゴリは必須
    if (!businessCategory) {
      setErrorAndScroll('事業カテゴリは必須です');
      return false;
    }

    // プリセールスフェーズの場合、新規登録理由が必須
    if (isPresalesPhase && !initialRegistrationReason.trim()) {
      setErrorAndScroll('プリセールスフェーズでは新規登録理由が必須です');
      return false;
    }

    // 次回アクションは常に必須
    if (!nextActionDate) {
      setErrorAndScroll('次回アクション予定日は必須です');
      return false;
    }
    if (!nextActionMemo) {
      setErrorAndScroll('次回アクション内容は必須です');
      return false;
    }

    // 実施フェーズの場合、外部プロジェクトIDが必須
    if (isExecutionPhase && !externalProjectId) {
      setErrorAndScroll('実施フェーズでは外部プロジェクトID（原価システム）が必須です');
      return false;
    }

    return true;
  };

  // 要員配置から金額を計算するヘルパー
  const calculateTotalAmount = (resources: Partial<ResourceAllocation>[]) => {
    return resources.reduce((sum, r) => {
      if (!r.unit_price || !r.start_month || !r.end_month) return sum;
      const startDate = new Date(r.start_month);
      const endDate = new Date(r.end_month);
      const months = (endDate.getFullYear() * 12 + endDate.getMonth()) - (startDate.getFullYear() * 12 + startDate.getMonth()) + 1;
      return sum + (r.unit_price * months);
    }, 0);
  };

  // 承認が必要な変更かどうかをチェック
  const hasApprovalRequiredChanges = () => {
    if (!salesCase) return false;
    // 新規下書き・却下後は常に再申請（正本へ直接書き込まない）
    if (salesCase.approval_status === 'draft' || salesCase.approval_status === 'rejected') {
      return true;
    }
    
    const isPhaseChanged = currentPhase !== originalPhase;
    const originalAmount = calculateTotalAmount(originalResourcesData);
    const newAmount = calculateTotalAmount(resourcesData);
    const isAmountChanged = Math.abs(originalAmount - newAmount) > 0.01;
    return isPhaseChanged || isAmountChanged;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    // 承認が必要な変更がある場合は承認ダイアログを表示
    if (hasApprovalRequiredChanges()) {
      await fetchApprovers();
      setApprovalDialogOpen(true);
      return;
    }

    // 承認不要の変更は直接適用
    await submitDirectUpdate();
  };

  // 承認不要の変更を直接適用
  const submitDirectUpdate = async () => {
    try {
      setSaving(true);

      const isPhaseChanged = currentPhase !== originalPhase;

      // 金額フィールドは要員配置から動的計算されるため送信不要
      const updateData: any = {
        case_name: caseName,
        case_summary: caseOverview,
        account_id: accountId as number,
        service_department: serviceDivision || undefined,
        end_user: endUser || undefined,
        // case_sourceは廃止予定のため除外
        business_category: businessCategory || undefined,
        assigned_employee_id: salesPersonId,
        sales_phase_id: currentPhase as number,
        expected_order_date: earliestStartMonth ? `${earliestStartMonth}-01` : undefined,
        external_project_id: externalProjectId || undefined,
        status: status,
        next_action_date: nextActionDate || undefined,
        next_action_memo: nextActionMemo || undefined,
      };

      // クローズ時の追加データ
      if (status === 'closed') {
        updateData.close_result = closeResult;
        updateData.close_reason_codes = selectedCloseReasonCodes;
        if (closeReasonText) {
          updateData.close_reason = closeReasonText;
        }
      }

      // フェーズ変更時は変更理由を追加
      if (isPhaseChanged) {
        updateData.change_reason = phaseChangeReason;
      }

      await salesCasesApi.updateCase(parseInt(caseId!), updateData);

      // 要員配置データを保存
      try {
        await salesCasesApi.saveCaseAllocations(parseInt(caseId!), resourcesData.map(r => ({
          employee_id: r.employee_id || undefined,
          resource_name: r.resource_name || 'TBA',
          resource_type: r.resource_type || 'employee',
          vendor_id: r.vendor_id,
          unit_price: r.unit_price || 0,
          cost_rate: r.cost_rate || 0,
          start_month: r.start_month || `${fiscalYear}-01-01`,
          end_month: r.end_month || `${fiscalYear}-12-01`,
        })));
      } catch (allocErr) {
        console.error('要員配置の保存に失敗しました:', allocErr);
        setErrorAndScroll(extractErrorMessage(allocErr, '案件は更新されましたが、要員配置の保存に失敗しました'));
        setSaving(false);
        return;
      }

      setSuccess('案件を更新しました');
      setTimeout(() => {
        navigate(`/sales/cases/${caseId}`);
      }, 1000);
    } catch (err) {
      console.error('案件更新エラー:', err);
      setErrorAndScroll(extractErrorMessage(err, '案件の更新に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // 承認申請を提出
  const handleApprovalSubmit = async () => {
    if (!selectedApprover) {
      setError('承認者を選択してください');
      return;
    }
    if (!salesCase) {
      setError('案件データが読み込まれていません');
      return;
    }

    try {
      setSaving(true);
      setApprovalDialogOpen(false);

      const approvedValues = salesCase.approved_values;
      const pendingOriginal = salesCase.pending_data?.original_values;
      const approvedPhaseId = approvedValues?.sales_phase_id ?? pendingOriginal?.sales_phase_id ?? originalPhase;
      const approvedPhaseName = approvedValues?.sales_phase_name
        ?? pendingOriginal?.sales_phase_name
        ?? phases.find(p => p.phase_id === approvedPhaseId)?.phase_name
        ?? '';
      const originalAllocationsSource = (salesCase.pending_data?.original_allocations && salesCase.pending_data.original_allocations.length > 0)
        ? salesCase.pending_data.original_allocations
        : originalResourcesData;
      const isPhaseChanged = currentPhase !== approvedPhaseId;
      const originalAmount = approvedValues?.expected_order_amount
        ?? pendingOriginal?.expected_order_amount
        ?? calculateTotalAmount(originalResourcesData);
      const newAmount = calculateTotalAmount(resourcesData);
      const isAmountChanged = Math.abs(originalAmount - newAmount) > 0.01;
      const currentPhaseObj = phases.find(p => p.phase_id === currentPhase);

      // 新規案件（draft/new_pending）かどうかを判定
      const isNewCase = salesCase.approval_status === 'draft' || salesCase.approval_status === 'new_pending';

      // 変更タイプを決定
      let requestType = 'other';
      if (isNewCase) {
        requestType = 'initial';
      } else if (isPhaseChanged && isAmountChanged) {
        requestType = 'phase_and_amount_change';
      } else if (isPhaseChanged) {
        requestType = 'phase_change';
      } else if (isAmountChanged) {
        requestType = 'resource_change';
      }

      // pending_dataを作成（統一構造）
      const pendingData = {
        request_type: requestType,
        original_values: isNewCase ? {} : {
          case_name: approvedValues?.case_name ?? pendingOriginal?.case_name ?? salesCase.case_name,
          sales_phase_id: approvedPhaseId,
          sales_phase_name: approvedPhaseName,
          assigned_employee_id: approvedValues?.assigned_employee_id
            ?? pendingOriginal?.assigned_employee_id
            ?? salesCase.assigned_employee_id,
          expected_order_amount: originalAmount,
          resource_count: originalAllocationsSource.length,
        },
        // 変更前の要員計画（正本）
        original_allocations: isNewCase ? [] : originalAllocationsSource.map(r => ({
          employee_id: r.employee_id || undefined,
          resource_name: r.resource_name || 'TBA',
          resource_type: r.resource_type || 'employee',
          vendor_id: r.vendor_id,
          unit_price: r.unit_price || 0,
          cost_rate: r.cost_rate || 0,
          start_month: r.start_month || '',
          end_month: r.end_month || '',
        })),
        new_values: {
          case_name: caseName,
          case_summary: caseOverview,
          account_id: accountId,
          account_name: salesCase.account_name || '',
          service_department: serviceDivision || '',
          end_user: endUser || '',
          business_category: businessCategory,
          sales_phase_id: currentPhase,
          sales_phase_name: currentPhaseObj?.phase_name || '',
          assigned_employee_id: salesPersonId,
          assigned_employee_name: salesCase.assigned_employee_name || '',
          expected_order_date: earliestStartMonth ? `${earliestStartMonth}-01` : '',
          next_action_date: nextActionDate || '',
          next_action_memo: nextActionMemo || '',
          external_project_id: externalProjectId || '',
          status: status,
          close_result: status === 'closed' ? closeResult : undefined,
          expected_order_amount: newAmount,
          gross_profit: calculatedFromResources.grossProfit,
          resource_count: resourcesData.length,
        },
        // 変更後の要員計画
        resource_allocations: resourcesData.map(r => ({
          employee_id: r.employee_id || undefined,
          resource_name: r.resource_name || 'TBA',
          resource_type: r.resource_type || 'employee',
          vendor_id: r.vendor_id,
          unit_price: r.unit_price || 0,
          cost_rate: r.cost_rate || 0,
          start_month: r.start_month || `${fiscalYear}-01-01`,
          end_month: r.end_month || `${fiscalYear}-12-01`,
        })),
        change_reason: phaseChangeReason || '',
      };

      // 承認申請APIを呼び出し
      const response = await salesApprovalsApi.submitApproval(parseInt(caseId!), {
        approver_id: selectedApprover,
        request_type: requestType,
        pending_data: pendingData,
        change_reason: phaseChangeReason || undefined,
        is_new_case: isNewCase, // 新規案件かどうかをバックエンドに伝える
      });

      if (response.data.success) {
        setSuccess('承認申請を提出しました');
        setTimeout(() => {
          navigate(`/sales/cases/${caseId}`);
        }, 1000);
      }
    } catch (err) {
      console.error('承認申請エラー:', err);
      setErrorAndScroll(extractErrorMessage(err, '承認申請の提出に失敗しました'));
    } finally {
      setSaving(false);
      setSelectedApprover('');
    }
  };

  // 承認申請取消
  const handleCancelApprovalRequest = async () => {
    try {
      setSaving(true);
      const response = await salesApprovalsApi.cancel(parseInt(caseId!));
      if (response.data.success) {
        setSuccess('承認申請を取り消しました');
        // 案件情報を再取得
        fetchCaseDetail();
        fetchAllocations();
      }
    } catch (err) {
      console.error('承認申請取消エラー:', err);
      setErrorAndScroll(extractErrorMessage(err, '承認申請の取り消しに失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // ステータス変更ハンドラ
  const handleStatusChange = (newStatus: string) => {
    if (newStatus === 'closed_won') {
      // 受注の場合は直接設定
      setStatus('closed');
      setCloseResult('won');
    } else if (newStatus === 'closed_lost') {
      // 失注の場合はダイアログで理由を選択
      setCloseResult('lost');
      setCloseDialogOpen(true);
    } else {
      setStatus(newStatus);
    }
  };

  // クローズダイアログの確定
  const handleCloseConfirm = () => {
    setStatus('closed');
    setCloseDialogOpen(false);
  };

  // クローズ理由チップのトグル
  const toggleCloseReason = (code: string) => {
    setSelectedCloseReasonCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  // カテゴリごとにグループ化
  const closeReasonsByCategory = useMemo(() => {
    const grouped: { [key: string]: CloseReason[] } = {};
    closeReasons.forEach(r => {
      if (!grouped[r.category]) grouped[r.category] = [];
      grouped[r.category].push(r);
    });
    return grouped;
  }, [closeReasons]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!salesCase) {
    return (
      <Box>
        <Alert severity="warning">案件が見つかりませんでした</Alert>
        <Button onClick={() => navigate('/sales/cases')} sx={{ mt: 2 }}>
          案件一覧に戻る
        </Button>
      </Box>
    );
  }

  const isPhaseChanged = currentPhase !== originalPhase;

  return (
    <Box ref={pageTopRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => setCancelDialogOpen(true)}>
          キャンセル
        </Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        営業案件 編集
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        案件No: {salesCase.case_no}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 承認待ち状態の警告 */}
      {(salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending') && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleCancelApprovalRequest}
              disabled={saving}
            >
              申請取消
            </Button>
          }
        >
          <strong>承認待ち状態</strong> - この案件は承認待ちのため編集できません。
          承認が完了するか、申請を取り消してから再度お試しください。
        </Alert>
      )}

      {/* 却下コメントの表示 */}
      {(salesCase.approval_status === 'draft' || salesCase.approval_status === 'rejected') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          表示・編集の対象は前回の申請内容です。保存すると再申請になります。正本は承認するまで変わりません。
          {salesCase.approval_comment && (
            <>
              <br />
              <strong>前回の承認申請について:</strong> {salesCase.approval_comment}
            </>
          )}
        </Alert>
      )}

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            基本情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="案件名" />
              <TextField
                value={caseName}
                onChange={(e) => setCaseName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
            </Box>
            <Box>
              <RequiredFieldLabel label="案件概要" />
              <TextField
                value={caseOverview}
                onChange={(e) => setCaseOverview(e.target.value)}
                multiline
                minRows={20}
                fullWidth
                inputProps={{ maxLength: 2000 }}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">案件状態</Typography>
                <RequiredBadge />
              </Box>
              {status === 'draft' ? (
                <Box>
                  <Chip label="新規承認待ち" color="secondary" />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ※ 承認されるまで案件状態は変更できません
                  </Typography>
                </Box>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Chip
                    label="商談中"
                    color={status === 'active' ? 'primary' : 'default'}
                    onClick={() => handleStatusChange('active')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="保留"
                    color={status === 'suspended' ? 'warning' : 'default'}
                    onClick={() => handleStatusChange('suspended')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="受注"
                    color={status === 'closed' && closeResult === 'won' ? 'success' : 'default'}
                    onClick={() => handleStatusChange('closed_won')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="失注"
                    color={status === 'closed' && closeResult === 'lost' ? 'error' : 'default'}
                    onClick={() => handleStatusChange('closed_lost')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Stack>
              )}
            </Box>

            {/* case_sourceは廃止予定（契約履歴から自動判定へ移行）*/}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">事業カテゴリ</Typography>
                <RequiredBadge />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label="SI経由開発"
                  color={businessCategory === 'SI経由開発' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('SI経由開発')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="日本国内開発"
                  color={businessCategory === '日本国内開発' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('日本国内開発')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="商品・サービス販売"
                  color={businessCategory === '商品・サービス販売' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('商品・サービス販売')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="SES"
                  color={businessCategory === 'SES' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('SES')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            {/* 営業フェーズ（Chip選択） */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">営業フェーズ</Typography>
                <RequiredBadge />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                現在: {phases.find(p => p.phase_id === originalPhase)?.phase_name || '-'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {phases.map((phase) => (
                  <Chip
                    key={phase.phase_id}
                    label={phase.phase_name}
                    color={currentPhase === phase.phase_id ? 'primary' : 'default'}
                    variant={currentPhase === phase.phase_id ? 'filled' : 'outlined'}
                    onClick={() => handlePhaseChange(phase.phase_id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: currentPhase === phase.phase_id ? phase.color_code : undefined,
                      '&:hover': { opacity: 0.8 },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* フェーズ変更理由（フェーズ変更時のみ表示） */}
            {isPhaseChanged && (
              <Box>
                <RequiredFieldLabel label="フェーズ変更理由" />
                <TextField
                  value={phaseChangeReason}
                  onChange={(e) => setPhaseChangeReason(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  inputProps={{ maxLength: 500 }}
                  helperText="※ フェーズを変更する場合、変更理由は必須です"
                />
              </Box>
            )}

            {/* 外部プロジェクトID（実施フェーズのみ表示・必須） */}
            {isExecutionPhase && (
              <Box>
                <RequiredFieldLabel label="外部プロジェクトID（原価システムから取得）" />
                <TextField
                  value={externalProjectId}
                  onChange={(e) => setExternalProjectId(e.target.value)}
                  fullWidth
                  helperText="原価システムのプロジェクトIDを入力してください"
                />
              </Box>
            )}

            {/* ネクストアクション操作ボタン */}
            {nextActionMode === 'view' && nextActionDate && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={handleActionComplete}
                  disabled={actionCompleting}
                >
                  {actionCompleting ? '登録中...' : '実施完了'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleActionPlanChange}
                >
                  計画変更
                </Button>
              </Box>
            )}

            {/* 次回アクション日 */}
            <Box>
              <RequiredFieldLabel label="次回アクション日" />
              <TextField
                type="date"
                value={nextActionDate}
                onChange={(e) => setNextActionDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: nextActionMode === 'view' }}
                sx={nextActionMode === 'view' ? { '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } } : {}}
              />
            </Box>

            {/* 次回アクション内容 */}
            <Box>
              <RequiredFieldLabel label="次回アクション内容" />
              <TextField
                value={nextActionMemo}
                onChange={(e) => setNextActionMemo(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ maxLength: 200, readOnly: nextActionMode === 'view' }}
                helperText={nextActionMode === 'edit' ? '次回予定しているアクション内容を記入' : '「実施完了」で行動履歴に登録、「計画変更」で編集'}
                sx={nextActionMode === 'view' ? { '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } } : {}}
              />
            </Box>

            {/* おすすめアクション */}
            {suggestedActions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  おすすめアクション（クリックで入力）
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {suggestedActions.map((action) => (
                    <Chip
                      key={action}
                      label={action}
                      size="small"
                      variant="outlined"
                      onClick={() => setNextActionMemo(prev => prev ? `${prev}、${action}` : action)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 要員計画 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            要員計画
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            案件に配置予定の要員情報を編集できます。詳細な月次工数は詳細画面の要員計画タブから入力してください。
          </Typography>
          <ResourceAllocationTable
            resources={resourcesData}
            onChange={setResourcesData}
            disabled={false}
            fiscalYear={fiscalYear}
            showAlert={true}
          />
        </CardContent>
      </Card>

      {/* 金額情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">金額情報</Typography>
              {resourcesData.length > 0 && (
                <Chip label="要員配置から自動計算" size="small" color="info" />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {fiscalYears.map((year) => (
                <Chip
                  key={year}
                  label={`${year}年`}
                  size="small"
                  color={fiscalYear === year ? 'primary' : 'default'}
                  variant={fiscalYear === year ? 'filled' : 'outlined'}
                  onClick={() => setFiscalYear(year)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>受注予定額</Typography>
                {!isPresalesPhase && <RequiredBadge />}
              </Box>
              <TextField
                type="number"
                value={resourcesData.length > 0 ? Math.floor(calculatedFromResources.sales).toString() : expectedOrderAmount}
                onChange={(e) => setExpectedOrderAmount(e.target.value)}
                fullWidth
                size="small"
                disabled={resourcesData.length > 0}
                InputProps={{ endAdornment: '円' }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="粗利額"
                type="number"
                value={resourcesData.length > 0 ? Math.floor(calculatedFromResources.grossProfit).toString() : grossProfit}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="受注予定月"
                type="month"
                value={earliestStartMonth || ''}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText={!earliestStartMonth && !isPresalesPhase ? '要員計画から自動設定' : undefined}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="期待売上"
                type="number"
                value={calculatedExpectedContribution.toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label={`${fiscalYear}年予定売上`}
                type="number"
                value={(yearlyTotals?.plannedSales || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年予定粗利`}
                type="number"
                value={(yearlyTotals?.plannedGrossProfit || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年期待売上`}
                type="number"
                value={((yearlyTotals?.plannedSales || 0) * (phases.find(p => p.phase_id === currentPhase)?.individual_conversion_rate || 0) / 100).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* 月別予実テーブル */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            月別予実（{fiscalYear}年）
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>月</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定外注率</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実外注率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyBreakdown.map((row) => (
                  <TableRow key={row.month} hover>
                    <TableCell>{row.month}月</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedSales)}</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedCost)}</TableCell>
                    <TableCell align="right">{row.plannedOutsourceRate.toFixed(0)}%</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualSales > 0 ? formatCurrencyWithUnit(row.actualSales) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? formatCurrencyWithUnit(row.actualCost) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? `${row.actualOutsourceRate.toFixed(0)}%` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* 合計行 */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedSales || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedCost || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {(yearlyTotals?.plannedCost || 0) > 0
                      ? `${((monthlyBreakdown.reduce((sum, m) => sum + (m.plannedOutsourceRate * m.plannedCost), 0) / (yearlyTotals?.plannedCost || 1))).toFixed(0)}%`
                      : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualSales || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualSales || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualCost || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualCost || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 行動履歴 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            行動履歴
          </Typography>
          <ActivityTimeline
            caseId={parseInt(caseId!)}
            activities={activities}
            readOnly={false}
            onAdd={handleAddActivity}
            onUpdate={handleUpdateActivity}
            onDelete={handleDeleteActivity}
          />
        </CardContent>
      </Card>

      {/* 営業担当者・取引先情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            営業担当者・取引先情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="営業担当者" />
              <TextField
                value={salesPerson}
                fullWidth
                disabled
                helperText="営業担当者は編集できません"
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>サービス部門</InputLabel>
              <Select
                value={serviceDivision}
                label="サービス部門"
                onChange={(e) => setServiceDivision(e.target.value)}
              >
                <MenuItem value="">
                  <em>選択してください</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_name}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <RequiredFieldLabel label="取引先" />
              <AsyncAutocomplete<AccountSearchResult>
              label=""
              searchApi={salesMasterApi.searchAccounts}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.account_name}
              value={selectedAccount}
              onChange={handleAccountSelect}
              placeholder="取引先名で検索..."
              freeSolo
              loadOnOpen
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' || typeof value === 'string') {
                  return false;
                }
                return option.account_id === value.account_id;
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.account_id}>
                  <Box>
                    <Typography variant="body2">{option.account_name}</Typography>
                    {option.account_name_kana && (
                      <Typography variant="caption" color="text.secondary">
                        {option.account_name_kana}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              />
            </Box>
            <TextField
              label="エンドユーザー"
              value={endUser}
              onChange={(e) => setEndUser(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RequiredBadge />
        <Typography variant="caption" color="text.secondary">
          は必須項目です
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => setCancelDialogOpen(true)} disabled={saving}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          disabled={saving || salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending'}
        >
          {saving ? '更新中...' : '更新する'}
        </Button>
      </Box>

      {/* キャンセル確認ダイアログ */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>編集内容の破棄</DialogTitle>
        <DialogContent>
          編集内容を破棄して詳細画面に戻りますか？
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>いいえ</Button>
          <Button onClick={() => navigate(`/sales/cases/${caseId}`)} color="primary">
            はい
          </Button>
        </DialogActions>
      </Dialog>

      {/* 原価システム整合性確認ダイアログ */}
      <Dialog open={costSystemConfirmOpen} onClose={handleCostSystemCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          【重要】原価システムとの整合性確認
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              このフェーズでは、原価システムとのデータ整合性が必要です。
              以下の情報が正確に入力されているか確認してください：
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                1. 外部プロジェクトID（原価システムのプロジェクトIDを使用してください）
              </Typography>
              <Typography variant="body1">
                2. 要員計画（原価システムのプロジェクト計画から取得ください）
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              ※ 粗利額・売上額は要員計画から自動計算されます。
              原価システムの情報と一致していることを確認してください。
            </Alert>

            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>受注確定</strong>：このフェーズへの変更により、案件は自動的に「受注」となります。
            </Alert>

            {/* 実行フェーズへの変更時のみ担当者変更オプションを表示 */}
            {isExecutionPhaseChange && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  担当者の変更（任意）
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  実行フェーズでは案件を技術部門に引き渡すため、担当者を変更できます。
                  元の営業担当者のフェーズ貢献は履歴として保持されます。
                </Typography>
                <FormControl fullWidth size="small">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={changeAssignee}
                        onChange={(e) => {
                          setChangeAssignee(e.target.checked);
                          if (!e.target.checked) {
                            setNewAssigneeId('');
                          }
                        }}
                      />
                    }
                    label="担当者を変更する"
                  />
                </FormControl>
                {changeAssignee && (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>新しい担当者</InputLabel>
                    <Select
                      value={newAssigneeId}
                      onChange={(e) => setNewAssigneeId(e.target.value)}
                      label="新しい担当者"
                    >
                      {assigneeList.map((assignee) => (
                        <MenuItem key={assignee.employee_id} value={assignee.employee_id}>
                          {assignee.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCostSystemCancel}>キャンセル</Button>
          <Button onClick={handleCostSystemConfirm} color="primary" variant="contained">
            確認しました
          </Button>
        </DialogActions>
      </Dialog>

      {/* 失注理由ダイアログ */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>失注理由の入力</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* 失注理由選択 */}
            {closeResult === 'lost' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  失注理由（複数選択可）
                </Typography>
                {Object.entries(closeReasonsByCategory).map(([category, reasons]) => (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {reasons.map(reason => (
                        <Chip
                          key={reason.reason_code}
                          label={reason.reason_name}
                          onClick={() => toggleCloseReason(reason.reason_code)}
                          color={selectedCloseReasonCodes.includes(reason.reason_code) ? 'error' : 'default'}
                          variant={selectedCloseReasonCodes.includes(reason.reason_code) ? 'filled' : 'outlined'}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <TextField
              label="補足（任意）"
              value={closeReasonText}
              onChange={(e) => setCloseReasonText(e.target.value)}
              multiline
              rows={2}
              fullWidth
              inputProps={{ maxLength: 500 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleCloseConfirm} color="primary" variant="contained">
            確定
          </Button>
        </DialogActions>
      </Dialog>

      {/* 承認者選択ダイアログ */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>承認者を選択してください</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            この変更は承認が必要です。承認者を選択してください。
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="approver-select-label">承認者</InputLabel>
            <Select
              labelId="approver-select-label"
              value={selectedApprover}
              onChange={(e) => setSelectedApprover(e.target.value)}
              label="承認者"
            >
              {approvers.map((approver) => (
                <MenuItem key={approver.employee_id} value={approver.employee_id}>
                  {approver.name} {approver.department_name && `(${approver.department_name})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setApprovalDialogOpen(false); setSelectedApprover(''); }}>
            キャンセル
          </Button>
          <Button
            onClick={handleApprovalSubmit}
            color="primary"
            variant="contained"
            disabled={!selectedApprover || saving}
          >
            {saving ? '提出中...' : '承認依頼を提出'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesCaseEditPage;
